import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import UserPicker from "../../component/admin/competitions/UserPicker";
import { getCompetition } from "../../api/getCompetition";
import { countryCodes } from "../../helper/commonFunctions";
import { ADVOCACY_PLATFORM_LABEL } from "../../utils/homeFlow";
import {
  accountEmail,
  accountFullName,
  contestantAgeValue,
  dobFromAge,
} from "../../utils/contestantProfile";
import {
  competitionMediaAccept,
  isCompetitionImageFile,
  isCompetitionVideoFile,
  resolveCompetitionMediaType,
} from "../../utils/competitionEventMedia";

const DEFAULT_COUNTRY_CODE = "+44 United Kingdom";
const MAX_VIDEO_SEC = 600;
const MAX_PROFILE_BYTES = 5 * 1024 * 1024;

function splitStoredMobile(stored) {
  const raw = String(stored || "").trim();
  if (!raw) return { countryCode: DEFAULT_COUNTRY_CODE, mobile: "" };
  const sorted = [...countryCodes].sort(
    (a, b) => b.split(" ")[0].length - a.split(" ")[0].length
  );
  for (const code of sorted) {
    const prefix = code.split(" ")[0];
    if (raw.startsWith(prefix)) {
      return {
        countryCode: code,
        mobile: raw.slice(prefix.length).replace(/\D/g, ""),
      };
    }
  }
  return {
    countryCode: DEFAULT_COUNTRY_CODE,
    mobile: raw.replace(/\D/g, ""),
  };
}

function probeClientDuration(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        const d = video.duration;
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(d) ? d : null);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      video.src = url;
    } catch (e) {
      resolve(null);
    }
  });
}

export default function AdminCompetitionRegister() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [competition, setCompetition] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [contestant, setContestant] = useState(null);

  const [fullName, setFullName] = useState("");
  const [age, setAge] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [bio, setBio] = useState("");
  const [hometown, setHometown] = useState("");
  const [profession, setProfession] = useState("");
  const [platformAdvocacy, setPlatformAdvocacy] = useState("");
  const [hobbies, setHobbies] = useState("");
  const [profileFile, setProfileFile] = useState(null);
  const [profilePreview, setProfilePreview] = useState("");

  const [eventId, setEventId] = useState("");
  const [category, setCategory] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [mediaKind, setMediaKind] = useState(null);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const isRegistered = Boolean(contestant?.id && contestant?.full_name);
  const lockedCategory = contestant?.category || "";
  const canUploadMedia = true;
  const categories = competition?.categories || [];
  const events = competition?.events || [];
  const myVideos = Array.isArray(contestant?.videos) ? contestant.videos : [];

  const lockedEventIds = useMemo(() => {
    const ids = new Set();
    myVideos.forEach((v) => {
      const st = String(v?.status || "").toLowerCase();
      if (v?.event_id != null && st !== "rejected") {
        ids.add(String(v.event_id));
      }
    });
    return ids;
  }, [myVideos]);

  const loadCompetition = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCompetition(id, token);
      if (!data.success) {
        toast.error(data.message || "Failed to load competition");
        return;
      }
      setCompetition(data.data);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load competition");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const loadContestant = useCallback(
    async (userId) => {
      if (!userId) {
        setContestant(null);
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getAdminContestant/${id}/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.data.success) {
          toast.error(res.data.message || "Failed to load contestant");
          return;
        }
        const mine = res.data.data?.contestant || null;
        setContestant(mine);
        const account = selectedUser || mine?.user;
        setProfilePreview(
          mine?.profile_image || account?.profile_img || ""
        );
        setProfileFile(null);
        setFullName(mine?.full_name || accountFullName(account) || "");
        setEmail(accountEmail(account) || mine?.email || "");
        if (mine?.full_name) {
          setAge(contestantAgeValue(mine) || "");
          const parsed = splitStoredMobile(mine.mobile);
          setCountryCode(parsed.countryCode);
          setMobile(parsed.mobile);
          setBio(mine.bio || "");
          setHometown(mine.hometown || "");
          setProfession(mine.profession || "");
          setPlatformAdvocacy(mine.platform_advocacy || "");
          setHobbies(mine.hobbies || "");
        } else {
          setAge(contestantAgeValue(account) || account?.age || "");
          const accountPhone = account?.phone_number || account?.mobile || "";
          if (accountPhone) {
            const parsed = splitStoredMobile(accountPhone);
            setCountryCode(parsed.countryCode);
            setMobile(parsed.mobile);
          } else {
            setMobile("");
            setCountryCode(DEFAULT_COUNTRY_CODE);
          }
          setBio(account?.bio || "");
          setHometown("");
          setProfession("");
          setPlatformAdvocacy("");
          setHobbies("");
        }
        if (mine?.category) {
          setCategory(mine.category);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load contestant");
      }
    },
    [
      id,
      token,
      selectedUser,
    ]
  );

  useEffect(() => {
    loadCompetition();
  }, [loadCompetition]);

  useEffect(() => {
    if (selectedUser?.id) {
      loadContestant(selectedUser.id);
    } else {
      setContestant(null);
    }
  }, [selectedUser, loadContestant]);

  useEffect(() => {
    if (lockedCategory) setCategory(lockedCategory);
  }, [lockedCategory]);

  const onUserChange = (user) => {
    setSelectedUser(user);
    setEventId("");
    setVideoFile(null);
    if (!user) {
      setContestant(null);
      setCategory("");
      setFullName("");
      setEmail("");
      setAge("");
      setMobile("");
      setCountryCode(DEFAULT_COUNTRY_CODE);
      setBio("");
      setHometown("");
      setProfession("");
      setPlatformAdvocacy("");
      setHobbies("");
      setProfilePreview("");
      setProfileFile(null);
      return;
    }
    setFullName(accountFullName(user));
    setEmail(accountEmail(user));
    setProfilePreview(user.profile_img || "");
    const accountPhone = user.phone_number || user.mobile || "";
    if (accountPhone) {
      const parsed = splitStoredMobile(accountPhone);
      setCountryCode(parsed.countryCode);
      setMobile(parsed.mobile);
    }
  };

  const onProfileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }
    if (file.size > MAX_PROFILE_BYTES) {
      toast.error("Profile picture must be 5 MB or less");
      return;
    }
    setProfileFile(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const saveProfile = async () => {
    if (!selectedUser?.id) {
      toast.error("Select a user first");
      return null;
    }
    if (
      !fullName.trim() ||
      !email.trim() ||
      !mobile ||
      !bio.trim() ||
      !hometown.trim() ||
      !profession.trim() ||
      !platformAdvocacy.trim() ||
      !hobbies.trim()
    ) {
      toast.error(
        `Full name, email, mobile, bio, hometown, profession, ${ADVOCACY_PLATFORM_LABEL.toLowerCase()}, and hobbies are required`
      );
      return null;
    }
    if (!isRegistered && !profileFile && !contestant?.profile_image) {
      toast.error("Profile picture is required");
      return null;
    }

    const data = new FormData();
    data.append("competition_id", id);
    data.append("user_id", selectedUser.id);
    data.append("full_name", fullName.trim());
    if (age) {
      data.append("age", age);
      const approxDob = dobFromAge(age);
      if (approxDob) data.append("dob", approxDob);
    }
    data.append("email", email.trim());
    data.append(
      "mobile",
      `${countryCode.split(" ")[0]}${String(mobile).replace(/\D/g, "")}`
    );
    data.append("bio", bio.trim());
    data.append("hometown", hometown.trim());
    data.append("profession", profession.trim());
    data.append("platform_advocacy", platformAdvocacy.trim());
    data.append("hobbies", hobbies.trim());
    if (profileFile) data.append("profile_image", profileFile);

    setSaving(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/adminUpsertContestant`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.data.success) {
        toast.error(res.data.message || "Failed to save profile");
        return null;
      }
      toast.success(res.data.message || "Profile saved");
      await loadContestant(selectedUser.id);
      return res.data.data;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to save profile");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const uploadVideo = async () => {
    if (!selectedUser?.id) {
      toast.error("Select a user first");
      return;
    }
    if (!canUploadMedia) {
      toast.error("Uploads are only allowed while the competition is running");
      return;
    }
    if (!eventId) {
      toast.error("Select an event");
      return;
    }
    if (!category) {
      toast.error("Select a category");
      return;
    }
    if (!videoFile) {
      toast.error("Choose a video or image file");
      return;
    }

    if (mediaKind === "video") {
      const duration = await probeClientDuration(videoFile);
      if (duration != null && duration > MAX_VIDEO_SEC) {
        toast.error("Video must be 10 minutes or less");
        return;
      }
    }

    const saved = await saveProfile();
    if (!saved && !isRegistered) return;

    const data = new FormData();
    data.append("competition_id", id);
    data.append("user_id", selectedUser.id);
    data.append("event_id", eventId);
    data.append("category", category);
    if (mediaKind === "image") {
      data.append("image", videoFile);
    } else {
      data.append("video", videoFile);
    }
    if (contestant?.id) data.append("contestant_id", contestant.id);
    const existingForEvent = myVideos.find(
      (v) =>
        String(v.event_id) === String(eventId) &&
        String(v.status || "").toLowerCase() !== "rejected"
    );
    if (existingForEvent?.id) {
      data.append("video_id", existingForEvent.id);
      data.append("replace_video_id", existingForEvent.id);
      data.append("replace", "1");
    }

    setSaving(true);
    setUploadProgress(0);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/adminUploadCompetitionEventVideo`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
          onUploadProgress: (ev) => {
            if (ev.total) {
              setUploadProgress(Math.round((ev.loaded * 100) / ev.total));
            }
          },
        }
      );
      if (!res.data.success) {
        toast.error(res.data.message || "Upload failed");
        return;
      }
      toast.success(res.data.message || "Upload submitted");
      setVideoFile(null);
      setVideoPreview("");
      setMediaKind(null);
      setEventId("");
      setUploadProgress(0);
      if (videoInputRef.current) videoInputRef.current.value = "";
      await loadContestant(selectedUser.id);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="page_title" textAlign="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="page_title">
      <ToastContainer />
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/dashboard/competitions")}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>
      </Stack>

      <Typography variant="h1" component="h1" mb={1}>
        Register contestant (admin)
      </Typography>
      <Typography sx={{ color: "#64748b", mb: 3 }}>
        {competition?.name || "Competition"} — search a user, save their profile,
        and upload event videos or images on their behalf.
      </Typography>

      {!canUploadMedia && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Video and image uploads are only available while the competition status is{" "}
          <strong>Running</strong>.
        </Alert>
      )}

      <Card sx={{ mb: 3, borderRadius: "12px", border: "1px solid #edf0f2" }}>
        <CardContent>
          <Typography sx={{ fontWeight: 700, mb: 2 }}>1. Select user</Typography>
          <UserPicker value={selectedUser} onChange={onUserChange} />
        </CardContent>
      </Card>

      {selectedUser && (
        <>
          <Card sx={{ mb: 3, borderRadius: "12px", border: "1px solid #edf0f2" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                2. Contestant profile
              </Typography>
              <Typography sx={{ color: "#64748b", fontSize: 13, mb: 2 }}>
                Email comes from the user account and cannot be changed. You can
                update the competition display name, profile fields, and media at
                any time (including approved entries).
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Full name"
                  size="small"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  helperText="Shown in the competition (contestant can also edit this)"
                  fullWidth
                />
                <TextField
                  label="Age"
                  type="number"
                  size="small"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  inputProps={{ min: 5, max: 100, step: 1 }}
                  fullWidth
                />
                <TextField
                  label="Email"
                  size="small"
                  value={email}
                  disabled
                  helperText="From selected user"
                  fullWidth
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    select
                    label="Country code"
                    size="small"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    sx={{ minWidth: 220 }}
                  >
                    {countryCodes.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Mobile"
                    size="small"
                    value={mobile}
                    onChange={(e) =>
                      setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                    }
                    fullWidth
                  />
                </Stack>

                <TextField
                  label="Bio"
                  size="small"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  multiline
                  minRows={3}
                  placeholder="Tell us about yourself…"
                  fullWidth
                />
                <TextField
                  label="Hometown"
                  size="small"
                  value={hometown}
                  onChange={(e) => setHometown(e.target.value)}
                  placeholder="e.g. London, UK"
                  fullWidth
                />
                <TextField
                  label="Profession"
                  size="small"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                  fullWidth
                />
                <TextField
                  label={ADVOCACY_PLATFORM_LABEL}
                  size="small"
                  value={platformAdvocacy}
                  onChange={(e) => setPlatformAdvocacy(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <TextField
                  label="Hobbies"
                  size="small"
                  value={hobbies}
                  onChange={(e) => setHobbies(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                    Profile picture {isRegistered ? "(editable)" : "(required)"}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      component="img"
                      src={profilePreview || "/placeholder.png"}
                      alt="Profile"
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #e2e8f0",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      sx={{ textTransform: "none" }}
                    >
                      Choose image
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={onProfileChange}
                      />
                    </Button>
                  </Stack>
                </Box>

                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={saveProfile}
                  sx={{ alignSelf: "flex-start", textTransform: "none" }}
                >
                  {saving ? "Saving..." : "Save profile"}
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: "12px", border: "1px solid #edf0f2" }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                3. Upload event video or image
              </Typography>
              {lockedCategory && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Category locked to <strong>{lockedCategory}</strong> for this
                  competition (set on first upload).
                </Alert>
              )}

              <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 2, mb: 1 }}>
                Event
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                {events.map((ev) => {
                  const locked = lockedEventIds.has(String(ev.id));
                  const selected = String(eventId) === String(ev.id);
                  return (
                    <Chip
                      key={ev.id}
                      label={locked ? `${ev.name} (replace)` : ev.name}
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      disabled={!canUploadMedia}
                      onClick={() => canUploadMedia && setEventId(String(ev.id))}
                    />
                  );
                })}
              </Stack>

              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                Category
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                {categories.map((c) => {
                  const cat = c.category;
                  const selected = category === cat;
                  const disabled =
                    Boolean(lockedCategory && lockedCategory !== cat) || !canUploadMedia;
                  return (
                    <Chip
                      key={cat}
                      label={cat}
                      color={selected ? "primary" : "default"}
                      variant={selected ? "filled" : "outlined"}
                      disabled={disabled}
                      onClick={() => !disabled && setCategory(cat)}
                    />
                  );
                })}
              </Stack>

              <Button
                variant="outlined"
                component="label"
                startIcon={<FileUploadIcon />}
                disabled={!canUploadMedia}
                sx={{ mb: 2, textTransform: "none" }}
              >
                {videoFile
                  ? videoFile.name
                  : "Choose video or image"}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept={competitionMediaAccept}
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    if (isCompetitionImageFile(file)) {
                      setMediaKind("image");
                      setVideoFile(file);
                      setVideoPreview(URL.createObjectURL(file));
                      return;
                    }
                    if (isCompetitionVideoFile(file)) {
                      setMediaKind("video");
                      setVideoFile(file);
                      setVideoPreview(URL.createObjectURL(file));
                      return;
                    }
                    toast.error(
                      "File type not supported. Upload a video or image (JPG, PNG, WEBP, GIF)."
                    );
                  }}
                />
              </Button>

              {videoPreview && (
                <Box sx={{ mb: 2, maxWidth: 360 }}>
                  {mediaKind === "image" ? (
                    <Box
                      component="img"
                      src={videoPreview}
                      alt="Preview"
                      sx={{
                        width: "100%",
                        maxHeight: 200,
                        objectFit: "contain",
                        borderRadius: 1,
                        bgcolor: "#111",
                      }}
                    />
                  ) : (
                    <Box
                      component="video"
                      src={videoPreview}
                      controls
                      muted
                      sx={{
                        width: "100%",
                        maxHeight: 200,
                        display: "block",
                        borderRadius: 1,
                        bgcolor: "#111",
                      }}
                    />
                  )}
                </Box>
              )}

              {uploadProgress > 0 && uploadProgress < 100 && (
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ mb: 2 }} />
              )}

              <Divider sx={{ my: 2 }} />

              {myVideos.length > 0 && (
                <Box mb={2}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                    Existing uploads
                  </Typography>
                  <Stack spacing={1}>
                    {myVideos.map((v) => {
                      const mediaType = resolveCompetitionMediaType(v);
                      return (
                      <Box
                        key={v.id}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          fontSize: 13,
                        }}
                      >
                        <strong>{v.event?.name || "Event"}</strong> · {v.category} ·{" "}
                        <Chip
                          size="small"
                          label={mediaType === "image" ? "Image" : "Video"}
                          sx={{ ml: 0.5 }}
                        />
                        <Chip size="small" label={v.status} sx={{ ml: 0.5 }} />
                      </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                disabled={saving || !canUploadMedia}
                onClick={uploadVideo}
                sx={{ textTransform: "none" }}
              >
                {saving ? "Uploading..." : "Save profile & upload"}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
